import moment from 'moment'
import { useEffect } from 'react'
import { type ApolloError } from '@apollo/client'
import { type LazyResponse } from '../../../../api/useApolloRequestLazyWrapper'
import { usePrevious } from '../../../../utils/usePrevious'
import { getFrigateQueryErrorType } from '../../../../utils/frigate/getQueryErrorType'
import { type GetDashboardOrderSearchResultListQueryVariables } from '../../../../../api'
import {
  type OrdersListRequestOrderType,
  type DashboardOrderSearchResultList,
  type OrdersListRequestOrderStatusCriteria,
} from '../types'
import { type TrackingEventMetadata } from '../../../../types'
import {
  type UserIdScenarioSuccessEventMetadata,
  type BaseSuccessEventMetadata,
  type ErrorEventMetadata,
  type SuccessEventMetadata,
} from './types'

function formatSuccessEventMetadata({
  queryParams,
  selectedStoreConfigurationId,
  retailerId,
  searchResults,
}: {
  queryParams: GetDashboardOrderSearchResultListQueryVariables
  selectedStoreConfigurationId?: string
  retailerId: string
  searchResults: DashboardOrderSearchResultList['results']
}): SuccessEventMetadata {
  const baseSuccessEventMetadata: BaseSuccessEventMetadata = {
    retailerId,
    numberOfOrders: searchResults?.length ?? 0,
    variables: JSON.stringify(queryParams),
  }

  if (queryParams?.fulfillmentDateScenario) {
    const dateRangeStart = queryParams.fulfillmentDateScenario.fulfillmentDateRange?.from
    const dateRangeEnd = queryParams.fulfillmentDateScenario.fulfillmentDateRange?.to
    const { orderStatusCriteria, orderType } = queryParams.fulfillmentDateScenario

    return {
      ...baseSuccessEventMetadata,
      scenario: 'fulfillmentDate',
      dateRange: Math.ceil(
        moment.duration(moment(dateRangeEnd).diff(moment(dateRangeStart))).asDays()
      ),
      numberOfRetailers: queryParams.fulfillmentDateScenario.retailerLocationIds?.length ?? 0,
      orderType: queryParams.fulfillmentDateScenario.orderType ?? '',
      numberOfStatuses: queryParams.fulfillmentDateScenario.orderStatusCriteria?.length ?? 0,
      numberOfOptionalFilters: calculateNumberOfOptionalFilters({
        orderStatusCriteria,
        orderType,
        selectedStoreConfigurationId,
      }),
    }
  }
  if (queryParams?.creationDateScenario) {
    const dateRangeStart = queryParams.creationDateScenario.creationDateRange?.from
    const dateRangeEnd = queryParams.creationDateScenario.creationDateRange?.to
    const { orderStatusCriteria, orderType } = queryParams.creationDateScenario

    return {
      ...baseSuccessEventMetadata,
      scenario: 'creationDate',
      dateRange: Math.ceil(
        moment.duration(moment(dateRangeEnd).diff(moment(dateRangeStart))).asDays()
      ),
      numberOfRetailers: queryParams.creationDateScenario.retailerLocationIds?.length ?? 0,
      orderType: queryParams.creationDateScenario.orderType ?? '',
      numberOfStatuses: queryParams.creationDateScenario.orderStatusCriteria?.length ?? 0,
      numberOfOptionalFilters: calculateNumberOfOptionalFilters({
        orderStatusCriteria,
        orderType,
        selectedStoreConfigurationId,
      }),
    }
  }
  if (queryParams?.userIdScenario) {
    const { orderStatusCriteria, orderType } = queryParams.userIdScenario
    let dateType: UserIdScenarioSuccessEventMetadata['dateType'] | undefined
    let dateRangeStart: string | undefined
    let dateRangeEnd: string | undefined

    if (queryParams.userIdScenario.creationDateRange) {
      dateType = 'creationDate'
      dateRangeStart = queryParams.userIdScenario.creationDateRange.from
      dateRangeEnd = queryParams.userIdScenario.creationDateRange.to
    } else if (queryParams.userIdScenario.fulfillmentDateRange) {
      dateType = 'fulfillmentDate'
      dateRangeStart = queryParams.userIdScenario.fulfillmentDateRange.from
      dateRangeEnd = queryParams.userIdScenario.fulfillmentDateRange.to
    }

    return {
      ...baseSuccessEventMetadata,
      scenario: 'userId',
      dateType: dateType!,
      dateRange: Math.ceil(
        moment.duration(moment(dateRangeEnd).diff(moment(dateRangeStart))).asDays()
      ),
      numberOfRetailers: queryParams.userIdScenario.retailerLocationIds?.length ?? 0,
      orderType: queryParams.userIdScenario.orderType ?? '',
      numberOfStatuses: queryParams.userIdScenario.orderStatusCriteria?.length ?? 0,
      numberOfOptionalFilters: calculateNumberOfOptionalFilters({
        orderStatusCriteria,
        orderType,
        selectedStoreConfigurationId,
      }),
    }
  }
  if (queryParams?.orderIdScenario) {
    return {
      ...baseSuccessEventMetadata,
      scenario: 'orderId',
    }
  }

  return {
    ...baseSuccessEventMetadata,
    scenario: 'unspecified',
  }
}

function calculateNumberOfOptionalFilters({
  orderStatusCriteria,
  orderType,
  selectedStoreConfigurationId,
}: {
  orderStatusCriteria: OrdersListRequestOrderStatusCriteria[] | undefined
  orderType: OrdersListRequestOrderType | undefined
  selectedStoreConfigurationId?: string
}): number {
  return [
    Boolean(orderStatusCriteria?.length),
    Boolean(orderType),
    Boolean(selectedStoreConfigurationId),
  ].filter(Boolean).length
}

// TODO: remove export once legacy pages are removed
export function formatFailureEventMetadata({
  queryParams,
  selectedStoreConfigurationId,
  retailerId,
  error,
}: {
  queryParams: GetDashboardOrderSearchResultListQueryVariables
  selectedStoreConfigurationId?: string
  retailerId: string
  error: ApolloError
}): ErrorEventMetadata {
  return {
    ...formatSuccessEventMetadata({
      queryParams,
      retailerId,
      searchResults: [],
      selectedStoreConfigurationId,
    }),
    error: getFrigateQueryErrorType(error),
  }
}

export function useLogRequest({
  requestData,
  queryParams,
  retailerId,
  selectedStoreConfigurationId,
  trackEvent,
}: {
  requestData: LazyResponse<
    DashboardOrderSearchResultList,
    GetDashboardOrderSearchResultListQueryVariables
  >
  queryParams: GetDashboardOrderSearchResultListQueryVariables
  retailerId: string
  selectedStoreConfigurationId?: string
  trackEvent: (event: string, metadata: TrackingEventMetadata) => void
}) {
  const previousRequestData = usePrevious(requestData)

  useEffect(() => {
    if (previousRequestData?.type !== 'pending') {
      return
    }
    if (requestData.type === 'pending') {
      return
    }

    if (requestData.type === 'success') {
      const successEventMetadata = formatSuccessEventMetadata({
        queryParams,
        retailerId,
        searchResults: requestData.data.results,
        selectedStoreConfigurationId,
      })
      // legacy
      trackEvent?.('ordersSearch', successEventMetadata)
      trackEvent?.('getDashboardOrderSearchResultList', successEventMetadata)
    } else {
      const failureEventMetadata = formatFailureEventMetadata({
        queryParams,
        retailerId,
        error: requestData.error,
        selectedStoreConfigurationId,
      })

      trackEvent?.('getDashboardOrderSearchResultListError', failureEventMetadata)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestData.type])
}
